export const FOCUS_AREAS = [
  "anxiety",
  "panic_attacks",
  "depression",
  "burnout",
  "stress",
  "trauma",
  "grief_loss",
  "ocd",
  "addictions",
  "self_esteem",
  "boundaries",
  "relationships",
  "communication",
  "emotional_regulation",
  "phobias",
  "life_crises",
  "parenting",
  "identity",
  "anger",
  "shame_guilt",
  "lgbtq",
  "adolescents",
  "couples_issues",
  "eating_disorders",
  "sleep_problems",
  "narcissistic_abuse",
  "social_anxiety",
  "procrastination",
  "decision_making",
  "self_development",
  "childhood_development",
  "family_issues",
  "work_issues",
  "motivation",
  "loneliness",
] as const;

export type FocusArea = (typeof FOCUS_AREAS)[number];

const FOCUS_AREA_SET = new Set<string>(FOCUS_AREAS);
const MAX_FOCUS_AREAS = 6;

export function sanitizeFocusAreas(raw: string[]): FocusArea[] {
  const seen = new Set<string>();
  const result: FocusArea[] = [];
  for (const value of raw) {
    if (!FOCUS_AREA_SET.has(value) || seen.has(value)) continue;
    seen.add(value);
    result.push(value as FocusArea);
    if (result.length === MAX_FOCUS_AREAS) break;
  }
  return result;
}

export const MODALITIES = [
  "kbt",
  "rebt",
  "gestalt",
  "act",
  "cft",
  "dbt",
  "psychodynamic",
  "integrative",
  "systemic",
  "emdr",
  "mindfulness",
  "other",
  "unknown",
] as const;

export type Modality = (typeof MODALITIES)[number];

export const POPULATIONS = [
  "adults",
  "adolescents",
  "children",
  "parents",
  "couples",
  "lgbtq",
  "other",
] as const;

export type Population = (typeof POPULATIONS)[number];

export const THERAPY_TYPES = ["INDIVIDUAL", "COUPLES", "BOTH"] as const;
export type TherapyType = (typeof THERAPY_TYPES)[number];

export const GENDERS = ["FEMALE", "MALE", "UNSPECIFIED"] as const;
export type MatchGender = (typeof GENDERS)[number];

export const EXPERIENCE_BUCKETS = [
  "UNKNOWN",
  "0_2",
  "3_5",
  "6_10",
  "11_PLUS",
] as const;
export type ExperienceBucket = (typeof EXPERIENCE_BUCKETS)[number];

/**
 * Get experience bucket from years
 */
export function getExperienceBucket(years: number | null): ExperienceBucket {
  if (years == null) return "UNKNOWN";
  if (years <= 2) return "0_2";
  if (years <= 5) return "3_5";
  if (years <= 10) return "6_10";
  return "11_PLUS";
}

/**
 * Get therapy types from prices
 */
export function getTherapyTypes(
  hasIndividual: boolean,
  hasCouples: boolean,
): TherapyType {
  if (hasIndividual && hasCouples) return "BOTH";
  if (hasCouples) return "COUPLES";
  return "INDIVIDUAL";
}

/**
 * Map Gender enum to match gender
 */
export function mapGenderToMatchGender(
  gender: "MALE" | "FEMALE" | "OTHER" | "PREFER_NOT_TO_SAY" | null,
): MatchGender {
  if (gender === "FEMALE") return "FEMALE";
  if (gender === "MALE") return "MALE";
  return "UNSPECIFIED";
}
